import { useHttpClient } from '@/composables/useHttpClient';
import { usePlans } from '@/composables/usePlans';
import { VAT_FACTOR } from '@/constants.ts';
import { useCubeStore } from '@/stores/useCubeStore';
import { useSettingsStore } from '@/stores/useSettingsStore';
import type { HistoryDataItem } from '@/types/HistoryData';
import type { TradingStatisticsRequestParams } from '@/types/TradingStatisticsRequestParams';
import * as ClpClient from '@/util/ClpClient';
import moment from 'moment';

export function useBackend() {
  const { backendApi, backendV3Api } = useHttpClient()

  const getTradingStatistics = async (params: TradingStatisticsRequestParams) => {
    const cube = useCubeStore()
    try {
      const result = await backendApi.get('/app/getTradingStatistics', {
        params: {
          cubeName: cube.name,
          ...getTimespanParameters(params)
        },
      })

      if (result && !result.data.bSuccess) {
        throw new Error(result.data.message)
      }

      return result.data.data
    } catch (error) {
      throw error
    }
  }

  const getTradingStatisticsHighRes = async (
    params: TradingStatisticsRequestParams = {
      startDate: moment().startOf('day'),
      granularity: '15min'
    }
  ) => {
    const cube = useCubeStore()
    const startOfDay = moment().startOf('day')

    if (
      (params.startDate == undefined || startOfDay <= params.startDate) &&
      params.granularity === '15min'
    ) {
      const result = await backendApi.get('/app/getTradingStatisticsHighRes', {
        params: {
          cubeName: cube.name,
          ...getTimespanParameters4HighRes(params)
        },
      })

      if (result && !result.data.bSuccess) {
        throw new Error(result.data.message)
      }

      return result.data.data
    }
  }

  const getReads = async (
    params: TradingStatisticsRequestParams = {
      startDate: moment().startOf('day'),
      granularity: '15min'
    }
  ): Promise<HistoryDataItem[]> => {
    try {
      const cube = useCubeStore()

      const result = await backendV3Api.get(`/app/consumption/${cube.name}`, {
        params: {
          ...getTimespanParameters(params)
        },
      })

      if (result && !result.data.bSuccess) {
        throw new Error(result.data.message)
      }

      return result.data.data
    } catch (error) {
      throw error;
    }
  }

  const getFlexPrices = async (
    params: TradingStatisticsRequestParams = {
      startDate: moment().startOf('day'),
      granularity: '15min'
    }
  ) => {
    const cube = useCubeStore()
    const settingsStore = useSettingsStore()
    const { isFlexPlan: checkIsFlexPlan } = usePlans()
    const isFlexPlan = checkIsFlexPlan()
    let consumptionPriceTimeSeries = []
    let productionPriceTimeSeries = []

    if (isFlexPlan) {
      const flexName = (cube.planName as unknown as string)?.replace(/_/g, ' ')
      const today = moment(params.startDate).add(1, 'day').startOf('day').toDate()

      let in2days = moment(today).add(1, 'day').toDate()

      const result = await backendApi.get(
        `/app/Prices/${flexName}/${today.toISOString().split('T')[0]}/${in2days.toISOString().split('T')[0]}?cubeName=${cube.name}`
      )

      if (result.data?.data?.length > 0) {
        const series = result.data.data

        series.forEach((s) => {
          if (s.cons !== '') {
            if (s.cons < 0) {
              //no tax on negatie values
              consumptionPriceTimeSeries.push(s.cons / 10) //EUR/MWh to
            } else {
              consumptionPriceTimeSeries.push((s.cons / 10) * VAT_FACTOR) //EUR/MWh to
            }
          } else {
            consumptionPriceTimeSeries.push(null)
          }

          if (s.gen !== '') {
            if (s.gen < 0) {
              productionPriceTimeSeries.push((s.gen / -10) * VAT_FACTOR)
            } else {
              productionPriceTimeSeries.push(s.gen / -10)
            }
          } else {
            productionPriceTimeSeries.push(null)
          }
        })
      }
    }

    return {
      consumptionPriceTimeSeries,
      productionPriceTimeSeries,
      isFlexPlan
    }
  }

  const getClpValues = async (
    params: TradingStatisticsRequestParams = {
      startDate: moment().startOf('day'),
      granularity: '15min'
    }
  ) => {
    const cube = useCubeStore()
    let consumptionTimeSeries = []
    let productionTimeSeries = []

    if (params.granularity === '15min') {
      if (
        cube.cubeCLP.consumptionCLP &&
        cube.cubeCLP.energyUsageConsumptionMWh !== 0
      ) {
        consumptionTimeSeries = await ClpClient.getScaledCLPTimeSeries(
          cube.cubeCLP.consumptionCLP,
          params.startDate.toDate(),
          moment(params.startDate).add(1, 'day').toDate(),
          cube.cubeCLP.energyUsageConsumptionMWh * 1000
        )
      }

      if (
        cube.cubeCLP.productionCLP &&
        cube.cubeCLP.energyUsageProductionMWh !== 0
      ) {
        productionTimeSeries = await ClpClient.getScaledCLPTimeSeries(
          cube.cubeCLP.productionCLP,
          params.startDate.toDate(),
          moment(params.startDate).add(1, 'day').toDate(),
          Number(cube.cubeCLP.energyUsageProductionMWh as number) * 1000
        )
      }
    } else if (params.granularity === '1day') {
      if (
        cube.cubeCLP.consumptionCLP &&
        cube.cubeCLP.energyUsageConsumptionMWh !== 0
      ) {
        consumptionTimeSeries = await ClpClient.getScaledCLPTimeSeries(
          cube.cubeCLP.consumptionCLP,
          params.startDate.toDate(),
          moment(params.startDate).add(30, 'day').toDate(),
          cube.cubeCLP.energyUsageConsumptionMWh * 1000
        )
      }

      if (
        cube.cubeCLP.productionCLP &&
        cube.cubeCLP.energyUsageProductionMWh !== 0
      ) {
        productionTimeSeries = await ClpClient.getScaledCLPTimeSeries(
          cube.cubeCLP.productionCLP,
          params.startDate.toDate(),
          moment(params.startDate).add(30, 'day').toDate(),
          Number(cube.cubeCLP.energyUsageProductionMWh as number) * 1000
        )
      }
    }

    return {
      consumptionTimeSeries,
      productionTimeSeries
    }
  }

  const getTimespanParameters4HighRes = (
    params: TradingStatisticsRequestParams = {
      startDate: moment().startOf('day'),
      granularity: '15min'
    }
  ) => {
    const startTime = moment(params.startDate).format('YYYY-MM-DD')

    return {
      date: startTime,
      granularity: params.granularity
    }
  }

  const getTimespanParameters = (
    params: TradingStatisticsRequestParams = {
      startDate: moment().startOf('day'),
      granularity: '15min'
    }
  ) => {
    const startTime = moment(params.startDate).format('YYYY-MM-DDTHH:mm')

    let endTime = moment(startTime).add(1, params.timespan).format('YYYY-MM-DDTHH:mm')

    if (params.timespan == "day") {
      // add an extra 15 minutes at the end, otherwise backend returns only values until 23:45
      // TODO this should be fixed in the backend
      // endTime = moment(startTime)
      //     .add(1, params.timespan)
      //     .add(15, "minutes")
      //     .format('YYYY-MM-DDTHH:mm')
    }

    return {
      // The parameter is called startTime on the backend even tough it's a date
      // without time because 00:00h is implied.
      startTime: startTime,
      endTime: endTime,
      granularity: params.granularity
    }
  }

  return {
    getTradingStatistics,
    getTradingStatisticsHighRes,
    getReads,
    getClpValues,
    getFlexPrices
  }
}
